/**
 * Mask thumbnail generation utility.
 *
 * computeRegionGeometries() uses a two-phase algorithm:
 *
 *   Phase 1 (single pass over labelPlane): accumulate pixelCount + bbox per
 *   region. One O(W×H) loop, no per-region work.
 *
 *   Phase 2 (per region): nearest-neighbour thumbnail fill from the labelPlane
 *   area defined by the now-known bbox. This phase cannot be merged into
 *   Phase 1 because the bbox is unknown until Phase 1 completes.
 *   Cost is bounded by total thumbnail pixels (numRegions × thumbnailSize²),
 *   typically tiny (e.g. 32 regions × 24² ≈ 18 K reads), so the overall cost
 *   remains dominated by the single labelPlane pass.
 *
 * Pure function - no DOM globals beyond the ImageData constructor (available
 * in main thread, Web Worker, Tauri webview, and happy-dom test env).
 * No console.log, no side effects.
 */

/**
 * Per-region geometry and display metadata derived from a labelmap.
 * Computed via the two-phase algorithm described at the top of this file.
 */
export type RegionGeometry = {
  label: number;
  pixelCount: number;
  /** null when the region has no pixels in the labelPlane. */
  bbox: { minX: number; minY: number; maxX: number; maxY: number } | null;
  /** Small bitmap visualising the region's mask shape. */
  thumbnail: ImageData;
};

export type RegionGeometryParams = {
  labelPlane: Uint8Array;
  width: number;
  height: number;
  /** Labels to compute geometry for (typically the active palette). */
  regionLabels: readonly number[];
  /** Colour swatch for each label - used to tint in-region thumbnail pixels. */
  regionRgbByLabel: ReadonlyMap<number, [number, number, number]>;
  /** Target thumbnail edge length in pixels (e.g. 24). */
  thumbnailSize: number;
};

// ---------------------------------------------------------------------------
// Internal accumulator per label
// ---------------------------------------------------------------------------

type Accumulator = {
  pixelCount: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  hasPx: boolean;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Two-phase derivation from the full labelPlane. Returns one RegionGeometry
 * per requested label in the same order as `regionLabels`.
 *
 * Labels not present in the plane get pixelCount: 0, bbox: null, and an
 * all-transparent thumbnail of the requested size.
 *
 * Algorithm:
 *   Phase 1 (single pass over labelPlane):
 *     1. Allocate per-label accumulators (pixelCount, bbox).
 *     2. Iterate every (x, y) once. For each pixel: if its label is in
 *        regionLabels, increment pixelCount and expand the bbox.
 *   Phase 2 (per region - bounded by numRegions × thumbnailSize²):
 *     3. Build a nearest-neighbour thumbnail for each region from its bbox,
 *        preserving aspect ratio with alpha-0 padding. Requires the completed
 *        bbox from Phase 1, so cannot be merged with the labelPlane pass.
 *     4. Return the array in regionLabels order.
 */
export function computeRegionGeometries(params: RegionGeometryParams): RegionGeometry[] {
  const { labelPlane, width, height, regionLabels, regionRgbByLabel, thumbnailSize } = params;

  // --- Step 1: build a Set and a Map for O(1) lookup ---
  const labelSet = new Set(regionLabels);

  const accumulators = new Map<number, Accumulator>();
  for (const label of regionLabels) {
    accumulators.set(label, {
      pixelCount: 0,
      minX: width,
      minY: height,
      maxX: -1,
      maxY: -1,
      hasPx: false,
    });
  }

  // --- Step 2: single pass ---
  for (let y = 0; y < height; y++) {
    const rowBase = y * width;
    for (let x = 0; x < width; x++) {
      const label = labelPlane[rowBase + x]!;
      if (!labelSet.has(label)) continue;

      const acc = accumulators.get(label)!;
      acc.pixelCount++;
      acc.hasPx = true;
      if (x < acc.minX) acc.minX = x;
      if (x > acc.maxX) acc.maxX = x;
      if (y < acc.minY) acc.minY = y;
      if (y > acc.maxY) acc.maxY = y;
    }
  }

  // --- Step 3: build thumbnails ---
  const results: RegionGeometry[] = [];

  for (const label of regionLabels) {
    const acc = accumulators.get(label)!;
    const rgb = regionRgbByLabel.get(label) ?? [128, 128, 128];

    if (!acc.hasPx) {
      // Empty region - all-transparent thumbnail
      results.push({
        label,
        pixelCount: 0,
        bbox: null,
        thumbnail: makeClearThumbnail(thumbnailSize),
      });
      continue;
    }

    const bbox = { minX: acc.minX, minY: acc.minY, maxX: acc.maxX, maxY: acc.maxY };
    const thumbnail = buildThumbnail(labelPlane, width, label, bbox, rgb, thumbnailSize);

    results.push({
      label,
      pixelCount: acc.pixelCount,
      bbox,
      thumbnail,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Returns a thumbnailSize × thumbnailSize all-transparent ImageData.
 */
function makeClearThumbnail(size: number): ImageData {
  return new ImageData(new Uint8ClampedArray(size * size * 4), size, size);
}

/**
 * Nearest-neighbour downscale of the binary mask inside `bbox`.
 *
 * Aspect-ratio is preserved: the bbox is scaled so its longer dimension fills
 * `thumbnailSize`; the shorter dimension is centred, with alpha-0 padding on
 * both sides. This avoids distortion for highly elongated regions.
 *
 * In-region pixels (labelPlane value === label) receive `rgb` + alpha 255.
 * Out-of-region pixels remain alpha 0.
 */
function buildThumbnail(
  labelPlane: Uint8Array,
  planeWidth: number,
  label: number,
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
  rgb: [number, number, number],
  thumbnailSize: number,
): ImageData {
  const bw = bbox.maxX - bbox.minX + 1; // bbox width  (>= 1)
  const bh = bbox.maxY - bbox.minY + 1; // bbox height (>= 1)

  // Scale so the longer dimension fills thumbnailSize exactly.
  const scale = thumbnailSize / Math.max(bw, bh);
  const scaledW = bw * scale; // <= thumbnailSize
  const scaledH = bh * scale; // <= thumbnailSize

  // Centre the scaled bbox within the square thumbnail.
  const offsetX = (thumbnailSize - scaledW) / 2;
  const offsetY = (thumbnailSize - scaledH) / 2;

  const [r, g, b] = rgb;
  const data = new Uint8ClampedArray(thumbnailSize * thumbnailSize * 4);

  for (let ty = 0; ty < thumbnailSize; ty++) {
    for (let tx = 0; tx < thumbnailSize; tx++) {
      // Map thumbnail pixel back into the scaled bbox space.
      const bx = tx - offsetX;
      const by = ty - offsetY;

      // Outside the scaled bbox region → leave alpha 0.
      if (bx < 0 || by < 0 || bx >= scaledW || by >= scaledH) continue;

      // Nearest-neighbour: map back to labelPlane coordinates.
      // Add 0.5 so we sample from the pixel centre rather than the pixel edge,
      // which avoids top-left bias for small/thin regions.
      const srcX = bbox.minX + Math.floor((bx + 0.5) / scale);
      const srcY = bbox.minY + Math.floor((by + 0.5) / scale);

      // Clamp for safety (floating-point edge rounding).
      const clampedX = Math.min(srcX, bbox.maxX);
      const clampedY = Math.min(srcY, bbox.maxY);

      if (labelPlane[clampedY * planeWidth + clampedX] === label) {
        const i = (ty * thumbnailSize + tx) * 4;
        data[i]     = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = 255;
      }
      // else: leave alpha 0 (out-of-region pixel inside the bbox)
    }
  }

  return new ImageData(data, thumbnailSize, thumbnailSize);
}
